// Centralized Media Configuration
// Simple config to control all video and image compression site-wide

// ========================================
// MAIN CONFIGURATION - Edit these values
// ========================================

// Emergency disable - set to true if videos won't load
const DISABLE_VIDEO_OPTIMIZATION = false;

// Video settings - Less aggressive for compatibility
const VIDEO_QUALITY = 'auto:good';    // Quality: auto:low | auto:good | auto:best
const VIDEO_BITRATE = 800;           // Bitrate in kbps (increased for playability)
const VIDEO_FPS = 30;                // Frame rate (standard for compatibility)
const VIDEO_MAX_WIDTH = 900;         // Max width in pixels (c_limit preserves aspect)

// Image settings
const IMAGE_QUALITY = 'auto:good';    // Quality: auto:low | auto:good | auto:best
// 2400px covers 2x retina for our ~1200px hero/card containers. Next's
// <Image> will re-sample down to the actual rendered width via srcset, so
// mobile clients don't receive 2400px bytes — we just give Next a sharp
// upstream source. Height is intentionally unset so Cloudinary preserves
// the source image's aspect ratio.
const IMAGE_MAX_WIDTH = 2400;

// ========================================
// OPTIMIZATION FUNCTION - Don't edit
// ========================================

export const optimizeCloudinaryUrl = (url: string, isVideo: boolean = false): string => {
  if (!url || !url.includes('cloudinary.com')) return url;
  
  // Emergency bypass for videos if optimization is disabled
  if (isVideo && DISABLE_VIDEO_OPTIMIZATION) return url;
  
  // Check if URL already has transformations
  const hasTransformations = url.includes('/v_') || url.includes('/w_') || url.includes('/h_') || url.includes('/q_') || url.includes('/f_');
  if (hasTransformations) return url;
  
  // Split URL to insert transformations
  const parts = url.split('/upload/');
  if (parts.length !== 2) return url;
  
  const [baseUrl, assetPath] = parts;
  
  let optimizations;
  
  if (isVideo) {
    optimizations = [
      `q_${VIDEO_QUALITY}`,
      'f_auto:video',
      `w_${VIDEO_MAX_WIDTH}`,
      'c_limit',
      `br_${VIDEO_BITRATE}k`,
      `fps_${VIDEO_FPS}`,
      'fl_progressive'
    ].join(',');
  } else {
    // `c_limit` (not `c_scale`) preserves the source aspect ratio AND
    // refuses to upscale images that are already smaller than IMAGE_MAX_WIDTH.
    // Setting only `w_` with no `h_` lets Cloudinary scale the short edge
    // proportionally — no squashing / stretching.
    optimizations = [
      `q_${IMAGE_QUALITY}`,
      'f_auto',
      `w_${IMAGE_MAX_WIDTH}`,
      'c_limit',
      'fl_progressive'
    ].join(',');
  }
  
  return `${baseUrl}/upload/${optimizations}/${assetPath}`;
}; 